import { CLIENT_ID_HEADER, SELECTED_CLIENT_STORAGE_KEY } from '@/config/auth0.config'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { getClients } from '@/services/analytics.api'
import { usePermissions } from './usePermissions'

export function useClientContext() {
  const queryClient = useQueryClient()
  const { canSwitchClient, canAccessAllClients, assignedTenantIds, effectiveClientLabel } = usePermissions()
  const [selectedClientId, setSelectedClientIdState] = useState<string | null>(() =>
    localStorage.getItem(SELECTED_CLIENT_STORAGE_KEY),
  )

  const { data: clients = [], isLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: getClients,
    enabled: canSwitchClient,
    staleTime: 10 * 60 * 1000,
  })

  const showClientSwitcher = canSwitchClient && (isLoading || clients.length > 1 || canAccessAllClients)

  const setSelectedClientId = useCallback(
    (clientId: string | null) => {
      if (clientId) {
        localStorage.setItem(SELECTED_CLIENT_STORAGE_KEY, clientId)
      } else {
        localStorage.removeItem(SELECTED_CLIENT_STORAGE_KEY)
      }
      setSelectedClientIdState(clientId)
      queryClient.invalidateQueries()
    },
    [queryClient],
  )

  useEffect(() => {
    const stored = localStorage.getItem(SELECTED_CLIENT_STORAGE_KEY)
    setSelectedClientIdState(stored)
  }, [])

  useEffect(() => {
    if (!canSwitchClient) {
      if (selectedClientId) {
        localStorage.removeItem(SELECTED_CLIENT_STORAGE_KEY)
        setSelectedClientIdState(null)
      }
      return
    }

    if (!selectedClientId) return

    if (canAccessAllClients) {
      const exists = clients.some(client => client.tenant_id === selectedClientId)
      if (!exists) {
        localStorage.removeItem(SELECTED_CLIENT_STORAGE_KEY)
        setSelectedClientIdState(null)
      }
      return
    }

    if (!assignedTenantIds.includes(selectedClientId)) {
      localStorage.removeItem(SELECTED_CLIENT_STORAGE_KEY)
      setSelectedClientIdState(null)
    }
  }, [assignedTenantIds, canAccessAllClients, canSwitchClient, clients, selectedClientId])

  return {
    clients,
    isLoadingClients: isLoading,
    showClientSwitcher,
    selectedClientId,
    selectedTenantId: selectedClientId,
    setSelectedClientId,
    setSelectedTenantId: setSelectedClientId,
    effectiveClientLabel,
    canSwitchClient,
  }
}

export function getClientIdHeader(): Record<string, string> {
  const selected = localStorage.getItem(SELECTED_CLIENT_STORAGE_KEY)
  if (!selected) return {}
  return { [CLIENT_ID_HEADER]: selected }
}
